import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { supplierActions } from "../slices/supplier";
import { SupplierMapper } from "../../mappers/SupplierMapper";
import { getErrorMessage } from "../../utils/errorUtils";

import { SupplierRequestDto } from "../../shared/dtos/supplier/SupplierRequestDto";
import { SupplierResponseDto } from "../../shared/dtos/supplier/SupplierResponseDto";
import { SupplierSummaryDto } from "../../shared/dtos/supplier/SupplierSummaryDto";
import { SupplierContactRequestDto } from "../../shared/dtos/supplierContact/SupplierContactRequestDto";
import { UpdateSupplierPayload } from "../../shared/interfaces/supplier/UpdateSupplierPayload";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getSuppliers =
  (params?: PaginationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(supplierActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<SupplierSummaryDto>>
      >("/suppliers", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(supplierActions.setItems(data.data.content));
        dispatch(supplierActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(supplierActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getSupplierById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(supplierActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<SupplierResponseDto>
      >(`/suppliers/${id}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(supplierActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(supplierActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createSupplier =
  (values: SupplierRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(supplierActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<SupplierResponseDto>
      >("/suppliers", values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Supplier created successfully!");
        const summary = SupplierMapper.mapToSummary(data.data);
        if (summary) dispatch(supplierActions.createSuccess(summary));
        dispatch(
          supplierActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(supplierActions.setErrorCreate(message));
    }
  };

export const updateSupplier =
  (payload: UpdateSupplierPayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(supplierActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<SupplierResponseDto>
      >(`/suppliers/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Supplier updated successfully!");
        const summary = SupplierMapper.mapToSummary(data.data);
        if (summary) dispatch(supplierActions.updateSuccess(summary));
        dispatch(
          supplierActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(supplierActions.setErrorUpdate(message));
    }
  };

export const deleteSupplier =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(supplierActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/suppliers/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Supplier deleted successfully!");
        dispatch(supplierActions.deleteSuccess(id));
        dispatch(
          supplierActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(supplierActions.setErrorDelete(message));
    }
  };

export const addSupplierContact =
  (values: SupplierContactRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(supplierActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<SupplierResponseDto>
      >("/suppliers/supplier-contacts", values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success("Contact added successfully!");
        const summary = SupplierMapper.mapToSummary(data.data);
        if (summary) dispatch(supplierActions.updateSuccess(summary));
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(supplierActions.setErrorUpdate(message));
    }
  };

export const removeSupplierContact =
  (supplierId: string, contactId: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(supplierActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.delete<
        ApiResponseDto<SupplierResponseDto>
      >(`/suppliers/${supplierId}/contacts/${contactId}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success("Contact removed successfully!");
        const summary = SupplierMapper.mapToSummary(data.data);
        if (summary) dispatch(supplierActions.updateSuccess(summary));
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(supplierActions.setErrorUpdate(message));
    }
  };

export const resetSupplierServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(supplierActions.setServerStatus(null));
  };
